import React from 'react';
import './style.css'
import AISearchView from './AISearchView';
import { config } from './config';

import {
 Typography,
 Container,
 Box,
 Paper,
 Grid,
 Button,
 List,
 ListItem,
 ListItemText,
 Divider,
 TextField,
 InputAdornment,
 IconButton,
 Chip,
 Stack,
 Select,
 MenuItem,
 FormControl,
 InputLabel,
 CircularProgress,
 Alert,
 Snackbar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API_BASE_URL = config.apiUrl;

function App() {
 const [papers, setPapers] = React.useState([]);
 const [loading, setLoading] = React.useState(true);
 const [error, setError] = React.useState(null);
 const [selectedPaper, setSelectedPaper] = React.useState(null);
 const [searchQuery, setSearchQuery] = React.useState('');
 const [selectedTopic, setSelectedTopic] = React.useState('');
 const [selectedAuthor, setSelectedAuthor] = React.useState('');
 const [selectedYear, setSelectedYear] = React.useState('');
 const [showDownloadSnackbar, setShowDownloadSnackbar] = React.useState(false);

 // Server-side search results (used when the search box is non-empty)
 const [searchResults, setSearchResults] = React.useState([]);
 const [searchLoading, setSearchLoading] = React.useState(false);

 // Search mode state
 const [searchMode, setSearchMode] = React.useState('regular');
 const [llmSearchAvailable, setLlmSearchAvailable] = React.useState(false);
 
 // Pagination state
 const [currentPage, setCurrentPage] = React.useState(1);
 const papersPerPage = 10;

 // Check if LLM search is available
 React.useEffect(() => {
   const checkLlmSearchStatus = async () => {
     try {
       const response = await fetch(`${API_BASE_URL}/api/search/llm-search/status`);
       if (response.ok) {
         const status = await response.json();
         setLlmSearchAvailable(status.available || false);
       }
     } catch (error) {
       setLlmSearchAvailable(false);
     }
   };
   checkLlmSearchStatus();
 }, []);

 // Fetch papers from the API
 React.useEffect(() => {
   const fetchPapers = async () => {
     try {
       setLoading(true);
       console.log('Fetching papers from:', `${API_BASE_URL}/api/papers/?limit=5000`);
       const response = await fetch(`${API_BASE_URL}/api/papers/?limit=5000`);
       console.log('Response status:', response.status);
       if (!response.ok) {
         throw new Error(`HTTP error! status: ${response.status}`);
       }
             const responseData = await response.json();
      // API returns array directly, not wrapped in papers object
      const papersData = Array.isArray(responseData) ? responseData : (responseData.papers || []);
      console.log('Papers data received:', papersData.length, 'papers');
      setPapers(papersData);
      if (papersData.length > 0) {
        setSelectedPaper(papersData[0]);
      }
     } catch (error) {
       console.error('Error fetching papers:', error);
       setError(`Failed to load papers from ${API_BASE_URL}. Error: ${error.message}`);
     } finally {
       setLoading(false);
     }
   };

   fetchPapers();
 }, []);

 // Server-side search. The backend matches title, abstract, author names,
 // author affiliations (e.g. "Qorvo"), and full paper text, with fuzzy matching
 // on company-name variants. Debounced; an empty query falls back to the full
 // corpus held in `papers`. Affiliation/full-text fields aren't shipped in the
 // list payload, so this match must run on the server, not client-side.
 React.useEffect(() => {
   const q = searchQuery.trim();
   if (!q) {
     setSearchResults([]);
     setSearchLoading(false);
     return;
   }
   setSearchLoading(true);
   const handle = setTimeout(async () => {
     try {
       const response = await fetch(`${API_BASE_URL}/api/papers/?search=${encodeURIComponent(q)}&limit=200`);
       if (!response.ok) {
         throw new Error(`HTTP error! status: ${response.status}`);
       }
       const data = await response.json();
       setSearchResults(Array.isArray(data) ? data : (data.papers || []));
     } catch (error) {
       console.error('Error searching papers:', error);
       setSearchResults([]);
     } finally {
       setSearchLoading(false);
     }
   }, 300);
   return () => clearTimeout(handle);
 }, [searchQuery]);

 // Download paper function
 const downloadPaper = async (paperId) => {
   try {
     const response = await fetch(`${API_BASE_URL}/api/papers/${paperId}/download-direct?source=keyword`);
     if (!response.ok) {
       throw new Error(`Download failed: ${response.status}`);
     }
     
     // Get the filename from the response headers
     const contentDisposition = response.headers.get('content-disposition');
     let filename = `paper_${paperId}.pdf`;
     if (contentDisposition) {
       const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
       if (filenameMatch) {
         filename = filenameMatch[1];
       }
     }
     
     // Create blob and download
     const blob = await response.blob();
     const url = window.URL.createObjectURL(blob);
     const a = document.createElement('a');
     a.href = url;
     a.download = filename;
     document.body.appendChild(a);
     a.click();
     window.URL.revokeObjectURL(url);
     document.body.removeChild(a);
     
     setShowDownloadSnackbar(true);
   } catch (error) {
     console.error('Error downloading paper:', error);
     setError('Failed to download paper. Please try again.');
   }
 };

 // Get unique values for filters from real data
 const allTopics = [...new Set(papers.flatMap(paper => paper.topics || []))];
 const lastNameOf = (author) => {
   if (!author) return '';
   // Comma-form: "Smith, John" → last name is before the comma
   if (author.includes(',')) {
     return author.split(',')[0].trim().toLowerCase();
   }
   // Strip trailing suffixes (Jr., Sr., II/III/IV, PhD, MD, Esq.) before taking the last token
   const cleaned = author.replace(/(\s+(jr|sr|i{2,3}|iv|v|phd|md|esq)\.?)+\s*$/i, '').trim();
   const tokens = cleaned.split(/\s+/);
   return (tokens[tokens.length - 1] || '').toLowerCase();
 };
 // Render "First Last" as "Last, First" so the alphabetical sort key is the first visible token.
 // Handles particles (van/de/der/von etc.) and preserves academic suffixes (Jr./PhD/III).
 const displayLastFirst = (author) => {
   if (!author) return '';
   if (author.includes(',')) return author;
   const suffixMatch = author.match(/(\s+((jr|sr|i{2,3}|iv|v|phd|md|esq)\.?\s*)+)$/i);
   const suffix = suffixMatch ? suffixMatch[0].trim() : '';
   const cleaned = suffix ? author.slice(0, suffixMatch.index).trim() : author.trim();
   const tokens = cleaned.split(/\s+/);
   if (tokens.length === 1) return suffix ? `${tokens[0]} ${suffix}` : tokens[0];
   if (!/^[A-Z]/.test(tokens[tokens.length - 1])) return author;
   let start = tokens.length - 1;
   while (start > 0 && /^[a-z]/.test(tokens[start - 1])) start--;
   const surname = tokens.slice(start).join(' ');
   const firstNames = tokens.slice(0, start).join(' ');
   if (!firstNames) return suffix ? `${surname} ${suffix}` : surname;
   return suffix ? `${surname}, ${firstNames} ${suffix}` : `${surname}, ${firstNames}`;
 };
 // Render the server's ts_headline snippet, turning [[hl]]...[[/hl]] markers into
 // <mark> highlights. Splitting on the markers (rather than injecting HTML) keeps this
 // XSS-safe: the matched terms are plain text, only the styling is ours.
 const renderMatchSnippet = (snippet) => {
   if (!snippet) return null;
   const parts = snippet.split(/\[\[hl\]\]|\[\[\/hl\]\]/);
   // ts_headline emits strictly alternating open/close markers, so odd-index parts are
   // the highlighted terms and even-index parts are the surrounding context.
   return parts.map((part, i) =>
     i % 2 === 1
       ? <mark key={i} style={{ backgroundColor: '#fef08a', color: 'inherit', padding: 0 }}>{part}</mark>
       : <React.Fragment key={i}>{part}</React.Fragment>
   );
 };
 const allAuthors = [...new Set(papers.flatMap(paper => paper.authors || []))]
   .sort((a, b) => {
     const cmp = lastNameOf(a).localeCompare(lastNameOf(b));
     return cmp !== 0 ? cmp : a.localeCompare(b);
   });
 const allYears = [...new Set(papers.map(paper => paper.year).filter(Boolean))].sort((a, b) => b - a);

 // The search query is matched server-side (title, abstract, authors, affiliations,
 // full text). When a query is present we filter over the server results; otherwise
 // over the full corpus. The topic/author/year dropdowns are applied client-side on
 // top of whichever base set is active.
 const hasQuery = searchQuery.trim().length > 0;
 const searchBase = hasQuery ? searchResults : papers;
 const filteredPapers = searchBase.filter(paper => {
   const matchesTopic = !selectedTopic || (paper.topics && paper.topics.includes(selectedTopic));
   const matchesAuthor = !selectedAuthor || (paper.authors && paper.authors.includes(selectedAuthor));
   const matchesYear = !selectedYear || paper.year === parseInt(selectedYear);

   return matchesTopic && matchesAuthor && matchesYear;
 });

// Reset to page 1 when filters change
React.useEffect(() => {
  setCurrentPage(1);
}, [searchQuery, selectedTopic, selectedAuthor, selectedYear]);

// Auto-select first paper when filtered results change
React.useEffect(() => {
  if (filteredPapers.length > 0) {
    setSelectedPaper(filteredPapers[0]);
  } else {
    setSelectedPaper(null);
  }
}, [searchQuery, selectedTopic, selectedAuthor, selectedYear, papers]);

// Pagination logic
 const totalPages = Math.ceil(filteredPapers.length / papersPerPage);
 const startIndex = (currentPage - 1) * papersPerPage;
 const endIndex = startIndex + papersPerPage;
 const currentPapers = filteredPapers.slice(startIndex, endIndex);

 // Pagination functions
 const handleNextPage = () => {
   if (currentPage < totalPages) {
     setCurrentPage(currentPage + 1);
     window.scrollTo({ top: 0, behavior: 'smooth' });
   }
 };

 const handlePrevPage = () => {
   if (currentPage > 1) {
     setCurrentPage(currentPage - 1);
     window.scrollTo({ top: 0, behavior: 'smooth' });
   }
 };

 // Reusable dropdown component props - configurable for different dropdown types
 const getDropdownProps = (options = {}) => {
   const {
     maxHeight = 300,
     maxWidth = 'auto',
     disablePortal = false,
     anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
     transformOrigin = { vertical: 'top', horizontal: 'left' }
   } = options;
   
   return {
     MenuProps: {
       PaperProps: {
         style: {
           maxHeight,
           maxWidth,
           overflow: 'auto',
         },
       },
       disablePortal,
       anchorOrigin,
       transformOrigin,
       // Prevent the dropdown from being cut off by parent containers
       style: {
         zIndex: 1300, // Higher than most other elements
       }
     },
   };
 };

 const clearFilters = () => {
   setSearchQuery('');
   setSelectedTopic('');
   setSelectedAuthor('');
   setSelectedYear('');
   setCurrentPage(1);
 };

return (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: "#ece9e1"
  }}>
    {/* Top utility bar */}
    <Box sx={{
      background: '#003ADD',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 4,
      py: 0.75,
    }}>
      <a href="https://csmantech.org" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
        <ArrowBackIcon sx={{ fontSize: '1rem' }} /> Back to CS MANTECH homepage
      </a>
      <Typography sx={{ color: 'white', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
        POWERED BY <strong>MIDWEST MICROELECTRONICS CONSORTIUM</strong>
      </Typography>
    </Box>

    {/* Hero banner */}
    <Box sx={{
      background: '#003ADD',
      display: 'flex',
      alignItems: 'center',
      px: 6,
      py: 3,
      gap: 3,
    }}>
      <Box sx={{
        width: 90,
        height: 90,
        borderRadius: '50%',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <img src="/cs-mantech-logo.png" height="70" width="73" alt="CS Mantech Logo" />
      </Box>
      <Box>
        <Typography variant="h3" component="div" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.1 }}>
          Digest <span style={{ color: '#FDC223' }}>Discovery</span>
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', mt: 0.75 }}>
          Search and explore the CS MANTECH technical digest archive
        </Typography>
      </Box>
    </Box>

    <Container 
     maxWidth="xl" 
     sx={{ 
       flex: 1,
       mt: 4, 
       mb: searchMode === 'llm' ? 2 : 6,
       px: { xs: 2, sm: 3, md: 4 },
       display: 'flex',
       flexDirection: 'column'
     }}
   >

    {/* Unified Modern Search Component */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      mb: searchMode === 'llm' ? 0 : 4
    }}>
      {searchMode === 'llm' ? (
        <>
          {/* Unified AI Search Card */}
          <Paper 
            elevation={0}
            sx={{ 
              width: '100%', 
              maxWidth: '1400px',
              bgcolor: '#ffffff',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }
            }}
          >
            {/* Toggle Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              mb: 2.5
            }}>
              <Box sx={{
                display: 'inline-flex',
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                padding: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <Button
                  variant={searchMode === 'regular' ? 'contained' : 'text'}
                  onClick={() => setSearchMode('regular')}
                  startIcon={<FilterListIcon sx={{ fontSize: '1.1rem' }} />}
                  sx={{
                    borderRadius: '8px',
                    px: 3.5,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    bgcolor: searchMode === 'regular' ? '#2563eb' : 'transparent',
                    color: searchMode === 'regular' ? '#ffffff' : '#64748b',
                    boxShadow: searchMode === 'regular' ? '0 2px 6px rgba(37,99,235,0.25)' : 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: '140px',
                    '&:hover': {
                      bgcolor: searchMode === 'regular' ? '#1d4ed8' : '#ffffff',
                      transform: searchMode === 'regular' ? 'none' : 'scale(1.02)'
                    }
                  }}
                >
                  Regular
                </Button>
                <Button
                  variant={searchMode === 'llm' ? 'contained' : 'text'}
                  onClick={() => { if (llmSearchAvailable) setSearchMode('llm'); }}
                  startIcon={<AutoAwesomeIcon sx={{ fontSize: '1.1rem' }} />}
                  disabled={!llmSearchAvailable}
                  sx={{
                    borderRadius: '8px',
                    px: 3.5,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    bgcolor: searchMode === 'llm' ? '#2563eb' : 'transparent',
                    color: searchMode === 'llm' ? '#ffffff' : '#64748b',
                    boxShadow: searchMode === 'llm' ? '0 2px 6px rgba(37,99,235,0.25)' : 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: '140px',
                    '&:hover': {
                      bgcolor: searchMode === 'llm' ? '#1d4ed8' : '#ffffff',
                      transform: llmSearchAvailable && searchMode !== 'llm' ? 'scale(1.02)' : 'none'
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent',
                      color: '#cbd5e1'
                    }
                  }}
                >
                  AI Search
                </Button>
              </Box>
            </Box>

            {/* AI Search View inside the card */}
            <AISearchView apiBaseUrl={API_BASE_URL} />
          </Paper>
        </>
      ) : (
        <>
          {/* Unified Search Card */}
          <Paper 
            elevation={0}
            sx={{ 
              width: '100%', 
              maxWidth: '1400px',
              bgcolor: '#ffffff',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              border: '1px solid #e5e7eb',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }
            }}
          >
            {/* Toggle Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              mb: 2.5
            }}>
              <Box sx={{
                display: 'inline-flex',
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                padding: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <Button
                  variant={searchMode === 'regular' ? 'contained' : 'text'}
                  onClick={() => setSearchMode('regular')}
                  startIcon={<FilterListIcon sx={{ fontSize: '1.1rem' }} />}
                  sx={{
                    borderRadius: '8px',
                    px: 3.5,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    bgcolor: searchMode === 'regular' ? '#2563eb' : 'transparent',
                    color: searchMode === 'regular' ? '#ffffff' : '#64748b',
                    boxShadow: searchMode === 'regular' ? '0 2px 6px rgba(37,99,235,0.25)' : 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: '140px',
                    '&:hover': {
                      bgcolor: searchMode === 'regular' ? '#1d4ed8' : '#ffffff',
                      transform: searchMode === 'regular' ? 'none' : 'scale(1.02)'
                    }
                  }}
                >
                  Regular
                </Button>
                <Button
                  variant={searchMode === 'llm' ? 'contained' : 'text'}
                  onClick={() => { if (llmSearchAvailable) setSearchMode('llm'); }}
                  startIcon={<AutoAwesomeIcon sx={{ fontSize: '1.1rem' }} />}
                  disabled={!llmSearchAvailable}
                  sx={{
                    borderRadius: '8px',
                    px: 3.5,
                    py: 1.2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    bgcolor: searchMode === 'llm' ? '#2563eb' : 'transparent',
                    color: searchMode === 'llm' ? '#ffffff' : '#64748b',
                    boxShadow: searchMode === 'llm' ? '0 2px 6px rgba(37,99,235,0.25)' : 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: '140px',
                    '&:hover': {
                      bgcolor: searchMode === 'llm' ? '#1d4ed8' : '#ffffff',
                      transform: llmSearchAvailable && searchMode !== 'llm' ? 'scale(1.02)' : 'none'
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent',
                      color: '#cbd5e1'
                    }
                  }}
                >
                  AI Search
                </Button>
              </Box>
            </Box>

            {/* Search Input */}
            <TextField
              fullWidth
              size="large"
              placeholder="Search by title, abstract, author, company/affiliation, or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f8fafc',
                  borderRadius: '14px',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                    borderWidth: '1px',
                  },
                  '&:hover': {
                    bgcolor: '#ffffff',
                    '& fieldset': {
                      borderColor: '#2563eb',
                    },
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                    '& fieldset': {
                      borderColor: '#2563eb',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '16px 18px',
                  '&::placeholder': {
                    color: '#94a3b8',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ 
                      color: '#64748b', 
                      fontSize: '1.5rem',
                      ml: 0.5
                    }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    {searchLoading && (
                      <CircularProgress size={18} sx={{ color: '#2563eb', mr: 1 }} />
                    )}
                    <IconButton
                      onClick={() => setSearchQuery('')}
                      sx={{
                        color: '#64748b',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#e5e7eb',
                          color: '#2563eb',
                        }
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Paper>
        </>
      )}
    </Box>

     {searchMode === 'regular' && (
       <>

         {/* Main Content Section: Two Column Layout */}
         <Box sx={{ 
           display: 'flex', 
           gap: 3,
           flexDirection: { xs: 'column', md: 'row' },
           maxWidth: '1400px',
           margin: '0 auto',
           width: '100%'
         }}>
         {/* Left Column: Filters and Paper List */}
         <Box sx={{ width: { xs: '100%', md: '35%' } }}>
           <Paper 
             elevation={0} 
             sx={{ 
               p: 3, 
               mb: 4,
               bgcolor: '#ffffff',
               borderRadius: '20px',
               boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
               border: '1px solid #e5e7eb',
               transition: 'all 0.3s ease',
               '&:hover': {
                 boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
               }
             }}
           >
             <Typography 
               variant="h6" 
               gutterBottom
               sx={{ 
                 fontWeight: 700,
                 color: '#1e293b',
                 mb: 3,
                 fontSize: '1.25rem'
               }}
             >
               Papers
             </Typography>
             {/* Filters */}
             <Stack spacing={2.5} sx={{ mb: 3 }}>
               <FormControl fullWidth size="medium">
                 <InputLabel sx={{ color: '#64748b' }}>Topic</InputLabel>
                 <Select
                   value={selectedTopic}
                   label="Topic"
                   onChange={(e) => setSelectedTopic(e.target.value)}
                   {...getDropdownProps({ 
                     maxHeight: 250,
                     maxWidth: 300
                   })}
                   sx={{
                     borderRadius: '12px',
                     bgcolor: '#f8fafc',
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#e5e7eb',
                     },
                     '&:hover .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#2563eb',
                     },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#2563eb',
                       borderWidth: '2px',
                     }
                   }}
                 >
                   <MenuItem value="">All Topics</MenuItem>
                   {allTopics.map(topic => (
                     <MenuItem key={topic} value={topic}>{topic}</MenuItem>
                   ))}
                 </Select>
               </FormControl>

               <FormControl fullWidth size="medium">
                 <InputLabel sx={{ color: '#64748b' }}>Author</InputLabel>
                 <Select
                   value={selectedAuthor}
                   label="Author"
                   onChange={(e) => setSelectedAuthor(e.target.value)}
                   renderValue={(v) => (v ? displayLastFirst(v) : '')}
                   {...getDropdownProps({ 
                     maxHeight: 300,
                     maxWidth: 400,
                     disablePortal: true
                   })}
                   sx={{
                     borderRadius: '12px',
                     bgcolor: '#f8fafc',
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#e5e7eb',
                     },
                     '&:hover .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#2563eb',
                     },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#2563eb',
                       borderWidth: '2px',
                     }
                   }}
                 >
                   <MenuItem value="">All Authors</MenuItem>
                   {allAuthors.map(author => (
                     <MenuItem key={author} value={author}>{displayLastFirst(author)}</MenuItem>
                   ))}
                 </Select>
               </FormControl>

               <FormControl fullWidth size="medium">
                 <InputLabel sx={{ color: '#64748b' }}>Year</InputLabel>
                 <Select
                   value={selectedYear}
                   label="Year"
                   onChange={(e) => setSelectedYear(e.target.value)}
                   {...getDropdownProps({ 
                     maxHeight: 200,
                     maxWidth: 150
                   })}
                   sx={{
                     borderRadius: '12px',
                     bgcolor: '#f8fafc',
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#e5e7eb',
                     },
                     '&:hover .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#2563eb',
                     },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#2563eb',
                       borderWidth: '2px',
                     }
                   }}
                 >
                   <MenuItem value="">All Years</MenuItem>
                   {allYears.map(year => (
                     <MenuItem key={year} value={year}>{year}</MenuItem>
                   ))}
                 </Select>
               </FormControl>

               {/* Results Counter */}
               <Box sx={{ 
                 display: 'flex', 
                 justifyContent: 'center', 
                 alignItems: 'center',
                 mt: 1,
                 p: 1.5,
                 backgroundColor: '#f8fafc',
                 borderRadius: '12px',
                 border: '1px solid #e5e7eb'
               }}>
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     fontWeight: 600,
                     color: '#64748b',
                     fontSize: '0.9rem'
                   }}
                 >
                   {loading ? 'Loading...' : `${filteredPapers.length} result${filteredPapers.length !== 1 ? 's' : ''} found`}
                 </Typography>
               </Box>

               {(searchQuery || selectedTopic || selectedAuthor || selectedYear) && (
                 <Button
                   size="medium"
                   onClick={clearFilters}
                   startIcon={<ClearIcon />}
                   sx={{
                     textTransform: 'none',
                     borderRadius: '12px',
                     py: 1.2,
                     fontWeight: 600,
                     color: '#64748b',
                     bgcolor: '#f8fafc',
                     border: '1px solid #e5e7eb',
                     '&:hover': {
                       bgcolor: '#ffffff',
                       color: '#2563eb',
                       borderColor: '#2563eb',
                     }
                   }}
                 >
                   Clear Filters
                 </Button>
               )}
             </Stack>

             {/* Active Filters */}
             {(selectedTopic || selectedAuthor || selectedYear) && (
               <Box sx={{ mb: 3 }}>
                 <Typography 
                   variant="body2" 
                   gutterBottom
                   sx={{ 
                     fontWeight: 600,
                     color: '#64748b',
                     mb: 1.5,
                     fontSize: '0.85rem'
                   }}
                 >
                   Active Filters:
                 </Typography>
                 <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                   {selectedTopic && (
                     <Chip
                       label={`Topic: ${selectedTopic}`}
                       onDelete={() => setSelectedTopic('')}
                       size="medium"
                       sx={{
                         bgcolor: '#eff6ff',
                         color: '#2563eb',
                         fontWeight: 500,
                         borderRadius: '8px',
                         '& .MuiChip-deleteIcon': {
                           color: '#2563eb',
                           '&:hover': {
                             color: '#1d4ed8',
                           }
                         }
                       }}
                     />
                   )}
                   {selectedAuthor && (
                     <Chip
                       label={`Author: ${displayLastFirst(selectedAuthor)}`}
                       onDelete={() => setSelectedAuthor('')}
                       size="medium"
                       sx={{
                         bgcolor: '#eff6ff',
                         color: '#2563eb',
                         fontWeight: 500,
                         borderRadius: '8px',
                         '& .MuiChip-deleteIcon': {
                           color: '#2563eb',
                           '&:hover': {
                             color: '#1d4ed8',
                           }
                         }
                       }}
                     />
                   )}
                   {selectedYear && (
                     <Chip
                       label={`Year: ${selectedYear}`}
                       onDelete={() => setSelectedYear('')}
                       size="medium"
                       sx={{
                         bgcolor: '#eff6ff',
                         color: '#2563eb',
                         fontWeight: 500,
                         borderRadius: '8px',
                         '& .MuiChip-deleteIcon': {
                           color: '#2563eb',
                           '&:hover': {
                             color: '#1d4ed8',
                           }
                         }
                       }}
                     />
                   )}
                 </Stack>
               </Box>
             )}

             {/* Paper List */}
             <List sx={{ px: 0 }}>
               {loading ? (
                 <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                   <CircularProgress sx={{ color: '#2563eb' }} />
                 </Box>
               ) : error ? (
                 <Alert 
                   severity="error" 
                   sx={{ 
                     mb: 2,
                     borderRadius: '12px',
                     border: '1px solid #fee2e2',
                     bgcolor: '#fef2f2'
                   }}
                 >
                   {error}
                 </Alert>
               ) : filteredPapers.length === 0 ? (
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     py: 4, 
                     textAlign: 'center',
                     color: '#94a3b8',
                     fontWeight: 500
                   }}
                 >
                   No papers found matching the criteria
                 </Typography>
               ) : (
                 currentPapers.map((paper, index) => (
                   <React.Fragment key={paper.cs_paper_id}>
                     <ListItem
                       button
                       selected={selectedPaper?.cs_paper_id === paper.cs_paper_id}
                       onClick={() => setSelectedPaper(paper)}
                       sx={{
                         borderRadius: '12px',
                         mb: 1,
                         transition: 'all 0.2s ease',
                         border: '1px solid transparent',
                         '&:hover': {
                           bgcolor: '#f8fafc',
                           border: '1px solid #e5e7eb',
                           transform: 'translateX(4px)',
                         },
                         '&.Mui-selected': {
                           bgcolor: '#eff6ff',
                           border: '1px solid #2563eb',
                           '&:hover': {
                             bgcolor: '#dbeafe',
                           }
                         }
                       }}
                     >
                       <ListItemText
                         primary={paper.title}
                         secondary={
                           <>
                             <span>{`${paper.authors?.[0] || ''} et al. (${paper.year || ''})`}</span>
                             {paper.match_snippet ? (
                               <span
                                 style={{
                                   display: '-webkit-box',
                                   WebkitLineClamp: 2,
                                   WebkitBoxOrient: 'vertical',
                                   overflow: 'hidden',
                                   marginTop: 4,
                                   fontSize: '0.8rem',
                                   fontStyle: 'italic',
                                   color: '#475569',
                                   lineHeight: 1.35,
                                 }}
                               >
                                 {renderMatchSnippet(paper.match_snippet)}
                               </span>
                             ) : null}
                           </>
                         }
                         primaryTypographyProps={{
                           fontWeight: selectedPaper?.cs_paper_id === paper.cs_paper_id ? 600 : 500,
                           fontSize: '0.95rem',
                           color: '#1e293b',
                           sx: {
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             display: '-webkit-box',
                             WebkitLineClamp: 2,
                             WebkitBoxOrient: 'vertical',
                           }
                         }}
                         secondaryTypographyProps={{
                           component: 'div',
                           fontSize: '0.85rem',
                           color: '#64748b',
                           fontWeight: 500
                         }}
                       />
                     </ListItem>
                   </React.Fragment>
                 ))
               )}
             </List>
             
             {/* Pagination Controls */}
             {totalPages > 1 && (
               <Box sx={{ 
                 display: 'flex', 
                 justifyContent: 'center', 
                 alignItems: 'center', 
                 mt: 3, 
                 pt: 3, 
                 borderTop: '2px solid #f1f5f9',
                 gap: { xs: 1.5, sm: 2 },
                 flexWrap: 'wrap'
               }}>
                 <Button
                   variant="outlined"
                   onClick={handlePrevPage}
                   disabled={currentPage === 1}
                   sx={{ 
                     minWidth: { xs: 80, sm: 90 },
                     borderRadius: '10px',
                     textTransform: 'none',
                     fontWeight: 600,
                     borderColor: '#e5e7eb',
                     color: '#64748b',
                     '&:hover': {
                       borderColor: '#2563eb',
                       color: '#2563eb',
                       bgcolor: '#f8fafc',
                     },
                     '&.Mui-disabled': {
                       borderColor: '#f1f5f9',
                       color: '#cbd5e1',
                     }
                   }}
                   size="medium"
                 >
                   Previous
                 </Button>
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     fontSize: { xs: '0.85rem', sm: '0.9rem' },
                     px: { xs: 1, sm: 2 },
                     fontWeight: 600,
                     color: '#64748b'
                   }}
                 >
                   Page {currentPage} of {totalPages}
                 </Typography>
                 <Button
                   variant="outlined"
                   onClick={handleNextPage}
                   disabled={currentPage === totalPages}
                   sx={{ 
                     minWidth: { xs: 80, sm: 90 },
                     borderRadius: '10px',
                     textTransform: 'none',
                     fontWeight: 600,
                     borderColor: '#e5e7eb',
                     color: '#64748b',
                     '&:hover': {
                       borderColor: '#2563eb',
                       color: '#2563eb',
                       bgcolor: '#f8fafc',
                     },
                     '&.Mui-disabled': {
                       borderColor: '#f1f5f9',
                       color: '#cbd5e1',
                     }
                   }}
                   size="medium"
                 >
                   Next
                 </Button>
               </Box>
             )}
           </Paper>
         </Box>

         {/* Right Column: Paper Preview */}
         <Box sx={{ width: { xs: '100%', md: '65%' } }}>
           <Paper 
             elevation={0} 
             sx={{ 
               p: 4, 
               height: 'fit-content',
               bgcolor: '#ffffff',
               borderRadius: '20px',
               boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
               border: '1px solid #e5e7eb',
               transition: 'all 0.3s ease',
               '&:hover': {
                 boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
               }
             }}
           >
             {selectedPaper ? (
               <Box>
                 <Typography 
                   variant="h5" 
                   gutterBottom
                   sx={{ 
                     fontWeight: 700,
                     color: '#1e293b',
                     mb: 2,
                     fontSize: '1.5rem',
                     lineHeight: 1.3
                   }}
                 >
                   {selectedPaper.title}
                 </Typography>
                 <Typography 
                   variant="subtitle1" 
                   gutterBottom
                   sx={{ 
                     color: '#64748b',
                     mb: 3,
                     fontSize: '1rem',
                     fontWeight: 500
                   }}
                 >
                   {selectedPaper.authors?.join(", ")}
                 </Typography>

                 {/* Info Grid */}
                 <Box sx={{ 
                   display: 'grid', 
                   gap: 2.5,
                   mb: 3
                 }}>
                   <Box sx={{ 
                     bgcolor: '#f8fafc',
                     p: 2,
                     borderRadius: '12px',
                     border: '1px solid #e5e7eb'
                   }}>
                     <Typography 
                       variant="body2" 
                       sx={{ 
                         fontWeight: 700,
                         color: '#475569',
                         mb: 0.5,
                         fontSize: '0.85rem',
                         textTransform: 'uppercase',
                         letterSpacing: '0.5px'
                       }}
                     >
                       Conference
                     </Typography>
                     <Typography 
                       variant="body1" 
                       sx={{ 
                         color: '#1e293b',
                         fontWeight: 500
                       }}
                     >
                       {selectedPaper.conference || 'N/A'}
                     </Typography>
                   </Box>

                   <Box sx={{ 
                     bgcolor: '#f8fafc',
                     p: 2,
                     borderRadius: '12px',
                     border: '1px solid #e5e7eb'
                   }}>
                     <Typography 
                       variant="body2" 
                       sx={{ 
                         fontWeight: 700,
                         color: '#475569',
                         mb: 0.5,
                         fontSize: '0.85rem',
                         textTransform: 'uppercase',
                         letterSpacing: '0.5px'
                       }}
                     >
                       Year
                     </Typography>
                     <Typography 
                       variant="body1" 
                       sx={{ 
                         color: '#1e293b',
                         fontWeight: 500
                       }}
                     >
                       {selectedPaper.year || 'N/A'}
                     </Typography>
                   </Box>

                   <Box sx={{ 
                     bgcolor: '#f8fafc',
                     p: 2,
                     borderRadius: '12px',
                     border: '1px solid #e5e7eb'
                   }}>
                     <Typography 
                       variant="body2" 
                       sx={{ 
                         fontWeight: 700,
                         color: '#475569',
                         mb: 1,
                         fontSize: '0.85rem',
                         textTransform: 'uppercase',
                         letterSpacing: '0.5px'
                       }}
                     >
                       Topics
                     </Typography>
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                       {selectedPaper.topics?.length > 0 ? (
                         selectedPaper.topics.map((topic, idx) => (
                           <Chip
                             key={idx}
                             label={topic}
                             size="small"
                             sx={{
                               bgcolor: '#eff6ff',
                               color: '#2563eb',
                               fontWeight: 500,
                               borderRadius: '8px',
                               fontSize: '0.85rem'
                             }}
                           />
                         ))
                       ) : (
                         <Typography variant="body1" sx={{ color: '#1e293b' }}>N/A</Typography>
                       )}
                     </Box>
                   </Box>

                   <Box sx={{ 
                     bgcolor: '#f8fafc',
                     p: 2,
                     borderRadius: '12px',
                     border: '1px solid #e5e7eb'
                   }}>
                     <Typography 
                       variant="body2" 
                       sx={{ 
                         fontWeight: 700,
                         color: '#475569',
                         mb: 1,
                         fontSize: '0.85rem',
                         textTransform: 'uppercase',
                         letterSpacing: '0.5px'
                       }}
                     >
                       Abstract
                     </Typography>
                     <Typography 
                       variant="body1" 
                       sx={{ 
                         color: '#475569',
                         lineHeight: 1.7,
                         fontSize: '0.95rem'
                       }}
                     >
                       {selectedPaper.abstract || 'N/A'}
                     </Typography>
                   </Box>
                 </Box>

                 <Button 
                   variant="contained" 
                   fullWidth 
                   size="large"
                   onClick={() => downloadPaper(selectedPaper.cs_paper_id)} 
                   startIcon={<DownloadIcon />}
                   sx={{ 
                     mt: 2,
                     py: 1.5,
                     backgroundColor: "#2563eb",
                     borderRadius: '12px',
                     textTransform: 'none',
                     fontSize: '1rem',
                     fontWeight: 600,
                     boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: "#1d4ed8",
                       boxShadow: '0 6px 16px rgba(37,99,235,0.4)',
                       transform: 'translateY(-2px)'
                     }
                   }}
                 >
                   Download PDF
                 </Button>
               </Box>
             ) : (
               <Box sx={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 justifyContent: 'center',
                 py: 8 
               }}>
                 <Typography 
                   variant="h6" 
                   gutterBottom
                   sx={{ 
                     color: '#94a3b8',
                     fontWeight: 600,
                     textAlign: 'center'
                   }}
                 >
                   Select a paper to view its details
                 </Typography>
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     color: '#cbd5e1',
                     textAlign: 'center',
                     mt: 1
                   }}
                 >
                   Choose from the list on the left
                 </Typography>
                 <img 
                   src="/paper-placeholder.png" 
                   alt="Paper Preview Placeholder" 
                   style={{ 
                     maxWidth: '100%', 
                     height: 'auto', 
                     marginTop: '40px',
                     opacity: 0.6
                   }} 
                   onError={(e) => { e.target.style.display = 'none'; }} 
                 />
               </Box>
             )}
           </Paper>
         </Box>
         </Box>
       </>
     )}
     </Container>
    <footer>
        <figure id="footer_logo" className= "flex-set flex--align-items-center">
          <a href="https://csmantech.org">
            <img src="cs-mantech-logo.png" height="100px" width="100px"></img>


          </a>
        </figure>
        <div id="footer_bottom" className="flex-set nav-columns flex--content-between flex--wrap">
          <div className="footer_left">
            <nav className="flex-set columns flex--wrap">
              <ul id="menu-footer-menu" className="menu">
                <li className="menu-item">
                  <a href="https://csmantech.org/about/">About</a>
                </li>
                <li className="menu-item">
                  <a href="https://csmantech.org/sponsors">Sponsors</a>
                </li>
              </ul>
              <ul className="menu">
                <li className="menu-item">
                  <a href="https://csmantech.org/digests/">Digests</a>
                </li>
                <li className="menu-item">
                    <a href="https://csmantech.org/contact/">Contact</a>
                </li>
              </ul>
            </nav>
          </div>
          <div className="footer_right">
            <nav>
              <ul className="menu">
                <li className="menu-item">
                  <a href="https://csmantech.org/terms-and-conditions/">Terms and Conditions</a>
                </li>
                <li className="menu-item">
                  <a href="https://csmantech.org/privacy-policy">Privacy Policy</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
    </footer>
     <Snackbar open={showDownloadSnackbar} autoHideDuration={6000} onClose={() => setShowDownloadSnackbar(false)}>
       <Alert onClose={() => setShowDownloadSnackbar(false)} severity="success" sx={{ width: '100%' }}>
         Paper downloaded successfully!
       </Alert>
     </Snackbar>
   </Box>
 );
}

export default App;

/*
 * ============================================================
 * OLD BANNER (white AppBar with full site navigation)
 * Preserved here for reference in case a rollback is needed.
 * ============================================================
 *
 * Required imports to restore:
 *   import { AppBar, Toolbar, ... } from '@mui/material';
 *   (AppBar and Toolbar were removed when the banner was replaced)
 *
 * JSX to restore (replace the two <Box> banner sections):
 *
 *   <AppBar sx={{position: "static", backgroundColor: "#f7fafa"}}>
 *     <Toolbar sx={{ minHeight: '180px', py: 4, px: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
 *       <Box sx={{ position: 'absolute', left: 60, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
 *         <figure id="logo" style={{ margin: 0, padding: 0 }}>
 *           <img src="/cs-mantech-logo.png" height="100px" width="105px" alt="CS Mantech Logo"></img>
 *         </figure>
 *         <Typography variant="h6" component="div" sx={{ color: "#0a0a0a", fontWeight: 500 }}>
 *           Conference Digest Database
 *         </Typography>
 *       </Box>
 *       <nav id="main_nav" style={{display: "flex", flexDirection: "row", gap: "1.5rem", alignItems:'center', margin: 0, padding: 5}}>
 *         <ul id="header_menu" className="flex-set font_medium" style={{display: "flex", gap: "1.5rem", flexDirection: "row"}}>
 *           <li>
 *             <a href="https://csmantech.org/about/">Explore <span style={{ fontSize: "0.8em", marginLeft: "0.3em" }}>▼</span></a>
 *             <div className="sub-menu-wrap">
 *               <div className="sub-menu-inner">
 *                 <div className="wrapper">
 *                   <ul className="sub-menu fancy_submenu">
 *                     <li className="menu-item"><a href="https://csmantech.org/about/">Our Mission</a></li>
 *                     <li className="menu-item"><a href="https://csmantech.org/board-of-directors/">Board of Directors</a></li>
 *                     <li className="menu-item"><a href="https://csmantech.org/executive-committee/">Executive Committee</a></li>
 *                     <li className="menu-item"><a href="https://csmantech.org/technical-program-committee/">Technical Program Committee</a></li>
 *                     <li className="menu-item"><a href="https://csmantech.org/2022-conference-collage/">2022 Conference Collage</a></li>
 *                   </ul>
 *                 </div>
 *               </div>
 *             </div>
 *           </li>
 *           <li><a href="https://csmantech.org/sponsors/">Sponsors</a></li>
 *           <li><a href="https://csmantech.org/exhibits/">Exhibitors</a></li>
 *           <li><a href="https://csmantech.org/authors/">Authors</a></li>
 *           <li><a href="https://csmantech.org/digests/">Digests</a></li>
 *           <li><a href="https://csmantech.org/contact/">Contact</a></li>
 *         </ul>
 *         <ul id="header_buttons" style={{display: "flex", flexDirection: "row", gap: "1rem"}}>
 *           <li><a className="button" href="https://www.bystadium.com/us/en/stores/cs-mantech-66106/S550175166">Swag Shop</a></li>
 *           <li><a className="button" href="https://cvent.me/N0ZEOY">Conference App</a></li>
 *         </ul>
 *       </nav>
 *     </Toolbar>
 *   </AppBar>
 *
 * CSS to restore in style.css (was removed alongside the banner):
 *
 *   #logo { margin: 0; padding: 0; gap: 1rem; }
 *   #main-nav { width: 100%; font-size: 20px; text-decoration: none; }
 *   #header_menu { list-style: none; margin: 0; padding: 0; }
 *   #header_menu li { position: relative; list-style: none; }
 *   #header_menu a { list-style: none; font-size: 20px; font-family: 'Grayfel', sans-serif; color: #585c59; text-decoration: none; }
 *   #header_buttons { color: #0078c1; padding: 15px 20px; list-style: none; margin: 0; }
 *   #header_buttons li { list-style: none; }
 *   .button { border-radius: 25px; display: inline-block; padding: 15px 20px; border: 1px solid; font-weight: bold; line-height: 1em; text-decoration: none; list-style: none; color: #0078c1; }
 *   .sub-menu-wrap { display: none; position: absolute; top: 100px; width: 100vw; z-index: 95; background-color: #fff; left: 0px; right: 0px; border-bottom: 1px solid #545454; border-top: 1px solid #545454; padding-top: 10px; padding-bottom: 10px; }
 *   li:hover > .sub-menu-wrap { display: block; }
 *
 * ============================================================
 */

